import { Env } from '@repo/types/config';
import { Themes } from '@repo/ui/theme';
import { nanoid } from 'nanoid';
import { createRoot } from 'react-dom/client';

import api from './utils/api';
import { getStudy } from './utils/fetchData';

export async function sendEvent(
    {
        analyticsId,
        event,
        studyId,
    }: {
        analyticsId: string;
        event: string;
        studyId: string;
    },
    demoMode: boolean,
    env: Env
) {
    if (demoMode) {
        return;
    }
    await api
        .post(`/v2/a/event`, env, {
            body: JSON.stringify({
                event: event,
                analyticsId: analyticsId,
                studyId: studyId,
            }),
        })
        .catch(err => console.error(`Error: ${err}`));
}

const analyticsId = nanoid();

function getEnvBasedOnUxsignalsEmbed() {
    const env = document
        .querySelectorAll<HTMLElement>('[data-uxsignals-embed]')[0]
        ?.getAttribute('data-uxsignals-env') as
        | 'test'
        | 'staging'
        | 'localhost'
        | 'production'
        | undefined;

    switch (env) {
        case 'localhost':
            return Env.Localhost;
        case 'test':
            return Env.Test;
        case 'staging':
            return Env.Staging;
        case 'production':
            return Env.Production;

        default:
            return Env.Production;
    }
}

async function main() {
    const elements = document.querySelectorAll<HTMLElement>('[data-uxsignals-embed]');

    for (const el of elements) {
        const id = el.getAttribute('data-uxsignals-embed');
        if (!id) {
            return;
        }
        const demoMode: boolean = el.getAttribute('data-uxsignals-mode') === 'demo';
        const fullPage: boolean = el.getAttribute('data-uxsignals-fullpage') === 'true';
        const env = getEnvBasedOnUxsignalsEmbed();
        const theme: Themes | null = el.getAttribute('data-uxsignals-theme') as Themes | null;
        const root = createRoot(el);
        const [study, { drawEmbed }] = await Promise.all([
            getStudy(id, demoMode, env),
            import('src/draw/drawEmbed'),
        ]);
        el.style.display = 'none';
        const isActive = study.active || demoMode;
        drawEmbed(
            root,
            fullPage,
            demoMode,
            analyticsId,
            { ...study, theme: theme || study.theme },
            env
        );

        if (isActive) {
            el.style.display = 'block';
            createObserver(el, () => {
                sendEvent({ analyticsId, studyId: study.id, event: 'embed.viewed' }, demoMode, env);
            });
            // We have clients who have more than 1 million events of this, and we dont use it for anything
            // So we're disabling it for now
            // sendEvent({ analyticsId, studyId: study.id, event: 'embed.rendered' }, demoMode);
        }
    }
}

function createObserver(element: HTMLElement, onView: () => void) {
    const observer = new IntersectionObserver(
        change => {
            if (change[0]?.isIntersecting) {
                onView();
                observer.disconnect();
            }
        },
        {
            root: null,
            rootMargin: '0px',
            threshold: 1.0,
        }
    );

    observer.observe(element);
}

main().catch(err => console.error(err));
